import mainStyle from "./mainStyle.module.scss";
import React, { useState, useEffect, useMemo } from "react";
import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Grid, Box, Button } from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";

import btn_news from "assets/images/icons/btn_help.svg";
import btn_news_light from "assets/images/icons/btn_help_light.svg";
import btn_announcement from "assets/images/icons/btn_announcement.svg";
import btn_announcement_light from "assets/images/icons/btn_announcement_light.svg";
import btn_resource_center from "assets/images/icons/btn_resource_center.svg";
import btn_resource_center_light from "assets/images/icons/btn_resource_center_light.svg";
import icn_expandright from "assets/images/icons/icn_expandright.svg";

// Service

import useDataSourceManager from "hooks/useDataSourceManager";

import News from "./News";
import Announcement from "./Announcement";

const Support = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const { dataMap, getData, sendRequest } = useDataSourceManager();

  const [newsUuid] = useState(uuidv4());
  const [announcementUuid] = useState(uuidv4());

  const [newsList, setNewsList] = useState([]);
  const [announcementList, setAnnouncementList] = useState([]);
  const [nowAnchorId, setNowAnchorId] = useState(null);
  const [openParent, setOpenParent] = useState("news");

  const getNews = () => sendRequest("/system/news.read", newsUuid);
  const getAnnouncement = () =>
    sendRequest("/system/announcement.read", announcementUuid);

  useEffect(() => {
    getNews();
  }, []);

  useEffect(() => {
    const newsResponse = getData(newsUuid);

    if (newsResponse?.data) {
      const sorted = [...(newsResponse.data || [])].sort(
        (a, b) =>
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      );

      setNewsList(sorted);
      getAnnouncement();
    }
  }, [dataMap]);

  useEffect(() => {
    const announcementResponse = getData(announcementUuid);

    if (announcementResponse?.data) {
      const sorted = [...(announcementResponse.data || [])].sort(
        (a, b) =>
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      );

      setAnnouncementList(sorted);
    }
  }, [dataMap]);

  useEffect(() => {
    if (openParent === "news") {
      if (newsList.length > 0 && nowAnchorId == null) {
        setNowAnchorId(newsList[0].id);
      }
    }

    if (openParent === "announcement") {
      if (announcementList.length > 0 && nowAnchorId == null) {
        setNowAnchorId(announcementList[0].id);
      }
    }
  }, [openParent, newsList, announcementList, nowAnchorId]);

  const handleSetNowAnchor = (id) => {
    setNowAnchorId(id);
    navigate(location.pathname, { replace: true, state: {} });
  };

  const handleParentClick = (parent) => {
    setOpenParent((prev) => (prev === parent ? null : parent));
    setNowAnchorId(null);
    navigate(location.pathname, { replace: true, state: {} });
  };

  const selectedNews = useMemo(
    () => newsList.find((n) => n.id === nowAnchorId) || null,
    [newsList, nowAnchorId],
  );

  const selectedAnnouncement = useMemo(
    () => announcementList.find((a) => a.id === nowAnchorId) || null,
    [announcementList, nowAnchorId],
  );

  const handleResourceCenterClick = () => {
    window.open(
      "https://www.dlink.com/en/for-business/nuclias/nuclias-unity-Resource-Center",
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <>
      <div className={mainStyle["mainArea"]}>
        <div className={mainStyle["anchorArea"]}>
          <div className={mainStyle["anchorBody"]}>
            <Box>
              <div
                className={mainStyle["anchorItem"]}
                style={{
                  backgroundColor:
                    openParent === "news" ? theme.palette.primary.main : "",
                  cursor: "pointer",
                }}
                onClick={() => handleParentClick("news")}
              >
                <img
                  src={openParent === "news" ? btn_news_light : btn_news}
                  alt="news"
                  style={{ width: "40px" }}
                />
                <span
                  className={mainStyle["anchorItem_text"]}
                  style={{
                    color:
                      openParent === "news"
                        ? theme.palette.primary.white
                        : theme.palette.primary.main,
                  }}
                >
                  What’s News
                </span>
              </div>

              {openParent === "news" &&
                newsList.map((item) => (
                  <div key={item.id}>
                    <div
                      className={mainStyle["subAnchorItem"]}
                      style={{
                        backgroundColor:
                          nowAnchorId === item.id
                            ? theme.palette.primary.subBlue
                            : "",
                      }}
                      onClick={() => handleSetNowAnchor(item.id)}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              backgroundColor: theme.palette.primary.main,
                              margin: "2.5px 10px 2.5px 0",
                              borderRadius: "2px",
                              display: "inline-block",
                            }}
                          />
                          <span
                            className={mainStyle["subAnchorItem_text"]}
                            style={{ color: theme.palette.primary.main }}
                            title={item.title}
                          >
                            {item.title}
                          </span>
                        </div>

                        <img src={icn_expandright} alt="" />
                      </div>
                    </div>
                  </div>
                ))}
            </Box>
            <Box>
              <div
                className={mainStyle["anchorItem"]}
                style={{
                  backgroundColor:
                    openParent === "announcement"
                      ? theme.palette.primary.main
                      : "",
                  cursor: "pointer",
                }}
                onClick={() => handleParentClick("announcement")}
              >
                <img
                  src={
                    openParent === "announcement"
                      ? btn_announcement_light
                      : btn_announcement
                  }
                  alt="announcement"
                  style={{ width: "40px" }}
                />
                <span
                  className={mainStyle["anchorItem_text"]}
                  style={{
                    color:
                      openParent === "announcement"
                        ? theme.palette.primary.white
                        : theme.palette.primary.main,
                  }}
                >
                  Announcement
                </span>
              </div>

              {openParent === "announcement" &&
                announcementList.map((item) => (
                  <div key={item.id}>
                    <div
                      className={mainStyle["subAnchorItem"]}
                      style={{
                        backgroundColor:
                          nowAnchorId === item.id
                            ? theme.palette.primary.subBlue
                            : "",
                      }}
                      onClick={() => handleSetNowAnchor(item.id)}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              backgroundColor: theme.palette.primary.main,
                              margin: "2.5px 10px 2.5px 0",
                              borderRadius: "2px",
                              display: "inline-block",
                            }}
                          />
                          <span
                            className={mainStyle["subAnchorItem_text"]}
                            style={{ color: theme.palette.primary.main }}
                            title={item.title}
                          >
                            {item.title}
                          </span>
                        </div>

                        <img src={icn_expandright} alt="" />
                      </div>
                    </div>
                  </div>
                ))}
            </Box>

            <Box>
              <div
                className={mainStyle["anchorItem"]}
                style={{
                  backgroundColor:
                    openParent === "resource" ? theme.palette.primary.main : "",
                  cursor: "pointer",
                }}
                onClick={handleResourceCenterClick}
              >
                <img
                  src={
                    openParent === "resource"
                      ? btn_resource_center_light
                      : btn_resource_center
                  }
                  alt="resource"
                  style={{ width: "40px" }}
                />
                <span
                  className={mainStyle["anchorItem_text"]}
                  style={{
                    color:
                      openParent === "resource"
                        ? theme.palette.primary.white
                        : theme.palette.primary.main,
                  }}
                >
                  Resource Center
                </span>
              </div>
            </Box>
          </div>
        </div>

        <div className={mainStyle["userArea"]}>
          <Grid style={{ marginBottom: "20px" }}>
            {openParent === "news" && <News selected={selectedNews} />}

            {openParent === "announcement" && (
              <Announcement selected={selectedAnnouncement} />
            )}
          </Grid>
        </div>
      </div>
    </>
  );
};

export default Support;
